import { interactionResponse } from '@zillow/yield-callback';
import { defineAnalytics, identifyUser } from '@zg-rentals/rentals-analytics';
import { tracker as CLICKSTREAM } from '@zg-rentals/clickstream';
import { tracker as COMSCORE } from '@/src/lib/comscore/tracker';
import type { ZrmUserInfo } from '@zg-rentals/clickstream';
import type { HotpadsClickstreamExtensions as HotpadsClickstreamExtensionsPayload } from './hotpads/types';
import { getHotpadsIdentityReady } from '@/src/lib/identity/browserIdentity';
import { getHotpadsComscoreReady } from '@/src/lib/comscore/script';

export const analytics = defineAnalytics({ CLICKSTREAM, COMSCORE });

const trackComscoreEventImmediately = analytics.createTrackEvent('COMSCORE');
const trackUserEventImmediately = analytics.createTrackEvent<'CLICKSTREAM', ZrmUserInfo>('CLICKSTREAM');
const trackHotpadsClickstreamEventImmediately = analytics.createTrackEvent<
  'CLICKSTREAM',
  HotpadsClickstreamExtensionsPayload
>('CLICKSTREAM');

let analyticsReady: Promise<void> | undefined;
let identityOverride: string | null | undefined;
let identifiedUserId: string | null | undefined;

export function updateHotpadsAnalyticsIdentity(userId: string | null): void {
  identityOverride = userId;
  if (identifiedUserId === undefined || identifiedUserId === userId) return;
  identifyUser(userId);
  identifiedUserId = userId;
}
export const IDENTITY_WAIT_DEADLINE_MS = 3000;

function waitForIdentityWithDeadline(identityReady: ReturnType<typeof getHotpadsIdentityReady>): Promise<{
  identity: Awaited<ReturnType<typeof getHotpadsIdentityReady>>;
  timedOut: boolean;
}> {
  return new Promise((resolve) => {
    let settled = false;
    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve({ identity: { status: 'unavailable', userToken: '' }, timedOut: true });
    }, IDENTITY_WAIT_DEADLINE_MS);

    void identityReady.then((identity) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve({ identity, timedOut: false });
    });
  });
}

function ensureHotpadsAnalyticsReady(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();

  analyticsReady ??= (() => {
    const identityReady = getHotpadsIdentityReady();
    return waitForIdentityWithDeadline(identityReady).then(({ identity, timedOut }) => {
      const userId =
        identityOverride === undefined
          ? identity.status === 'authenticated'
            ? identity.userToken
            : null
          : identityOverride;
      analytics.initialize({ brand: 'hotpads', userId });
      identifiedUserId = userId;

      if (timedOut) {
        void identityReady.then((lateIdentity) => {
          if (identityOverride === undefined && lateIdentity.status === 'authenticated') {
            identifiedUserId = lateIdentity.userToken;
            identifyUser(lateIdentity.userToken);
          }
        });
      }

      return undefined;
    });
  })();
  return analyticsReady;
}

function afterAnalyticsReady<Track extends (payload: never) => void>(track: Track): Track {
  return ((payload: Parameters<Track>[0]) => {
    if (typeof window === 'undefined') {
      track(payload);
      return;
    }

    // `interactionResponse` between the two thens is what keeps telemetry out of
    // INP. Awaiting readiness alone is not enough: once that promise is resolved
    // its `.then` is a microtask, which still runs before the interaction's
    // paint, so the emit lands inside the measured window.
    void ensureHotpadsAnalyticsReady()
      .then(interactionResponse)
      .then(() => track(payload));
  }) as Track;
}

export const trackComscoreEvent = ((payload: Parameters<typeof trackComscoreEventImmediately>[0]) => {
  if (typeof window === 'undefined') {
    trackComscoreEventImmediately(payload);
    return;
  }

  void Promise.all([ensureHotpadsAnalyticsReady(), getHotpadsComscoreReady()]).then(() =>
    trackComscoreEventImmediately(payload),
  );
}) as typeof trackComscoreEventImmediately;
export const trackUserEvent = afterAnalyticsReady(trackUserEventImmediately);
export const trackHotpadsClickstreamEvent = afterAnalyticsReady(trackHotpadsClickstreamEventImmediately);

export function initializeHotpadsAnalytics(): void {
  void ensureHotpadsAnalyticsReady();
}

export type {
  HotpadsClickstreamExtensions,
  HotpadsClickstreamListing,
  HotpadsContactRequestForm,
  HotpadsNextAttribution,
  HotpadsPropertyInfo,
} from './hotpads/types';
